import { Button, message, Tabs } from "antd";
import React, { useState, useEffect, useRef } from "react";
import ChiefComplaintsForm from "../OpFormComponents/ChiefComplaintsForm";
import OpHistoryForm from "../OpFormComponents/OpHistoryForm";
import NeuroHistory from "./NeuroHistory";
import OPExaminationForm from "../OpFormComponents/OPExaminationForm";
import OpReflexesForm from "../OpFormComponents/OpReflexesForm";
import OpFunctionalDiagnosis from "../CardiopulmonaryForm/OpFunctionalDiagnosis";
import NeuroTherapy from "./NeuroTherapy";
import OpExerciseForm from "../OpFormComponents/OpExercise/OpExerciseForm";
import ReviewNotes from "../OpFormComponents/ReviewNotes";
import NeuroSummary from "./NeuroSummary";
import { opServiceAPI } from "../../../../../services/opphysio.services";
import { theme } from "antd";
import PhysioScalesOp from "../../../../IpEMR/InspectionForms/Examination/PhysioScales/PhysioScalesOp";
import { opConfirmModal } from "../../opConfirmModal";
import "../../OPForms.css";

const SCALE_TAB_MAP = {
  4: "GCS",
  5: "RASS",
  6: "MMT",
  7: "MAS",
  8: "VCG",
  9: "MOCA",
  10: "FingerGripTest",
  11: "ROM",
  13: "CoordinationTest",
  14: "TrunkControlTest",
  15: "BarthelIndex",
  16: "ASIA",
  17: "NIHSS",
};
const SCALE_KEY_NORMALIZER = {
  rass: "ras_scale",
  fingergriptest: "finger_grip_test",
  coordinationtest: "coordination",
  trunkcontroltest: "tcms",
  barthelindex: "barthel",
  moca: "moca",
};

const tabItems = [
  { label: "Chief Complaints", key: "1" },
  { label: "History", key: "2" },
  { label: "Examination", key: "3" },
  { label: "GCS", key: "4" },
  { label: "RASS Scale", key: "5" },
  { label: "MMT", key: "6" },
  { label: "MAS", key: "7" },
  { label: "VCG", key: "8" },
  { label: "MOCA", key: "9" },
  { label: "Finger Grip Test", key: "10" },
  { label: "ROM", key: "11" },
  { label: "Reflexes", key: "12" },
  { label: "Coordination Test", key: "13" },
  { label: "TCMS", key: "14" },
  { label: "Barthel", key: "15" },
  { label: "ASIA", key: "16" },
  { label: "NIHSS", key: "17" },
  { label: "Functional Diagnosis", key: "18" },
  { label: "Therapy", key: "19" },
  { label: "Exercise", key: "20" },
  { label: "Review Notes", key: "21" },
  { label: "Summary", key: "22" },
];
const getTabLabel = (key) => {
  const tab = tabItems.find((item) => item.key === key);
  return tab?.label || "Form";
};

const NeuroForm = ({
  admissionId,
  handleClose,
  formData: initialData = {},
  onSave,
  patientData,
}) => {
  const { token } = theme.useToken();
  const [formData, setFormData] = useState(initialData);
  const lastSavedRef = useRef(initialData);
  const [activeTab, setActiveTab] = useState("1");
  const [isDirty, setIsDirty] = useState(false);
  const [activeScaleKey, setActiveScaleKey] = useState(null);
  const [scaleTab, setScaleTab] = useState("1");
  const [scalesId, setScalesId] = useState(null);
  const scaleSaveRef = useRef(null);

  const onValuesChange = (key, changedValues, meta) => {
    if (key === "exercise" && Array.isArray(changedValues)) {
      setFormData((prev) => ({
        ...prev,
        exercise: changedValues,
      }));
      if (meta?.hydrateOnly) {
        lastSavedRef.current = {
          ...(lastSavedRef.current || {}),
          exercise: changedValues,
        };
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          ...changedValues,
        },
      }));
      if (meta?.hydrateOnly) {
        lastSavedRef.current = {
          ...(lastSavedRef.current || {}),
          [key]: {
            ...((lastSavedRef.current || {})[key] || {}),
            ...changedValues,
          },
        };
      }
    }
    if (!meta?.hydrateOnly) {
      setIsDirty(true);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);
  const handleTabChange = (nextKey) => {
    const prevScaleKey = activeScaleKey;
    const nextScaleKey = SCALE_TAB_MAP[nextKey] || null;

    if (isDirty) {
      opConfirmModal({
        title: "Unsaved changes",
        content: "Save and proceed?",
        okButtonProps: {
          style: {
            backgroundColor: token.colorPrimary,
            borderColor: token.colorPrimary,
            color: "#fff",
          },
        },
        onOk: async () => {
          let payload = { ...formData };

          if (prevScaleKey && scaleSaveRef.current) {
            const scaleData = await scaleSaveRef.current.saveScale();

            if (scaleData) {
              const normalizedKey =
                SCALE_KEY_NORMALIZER[prevScaleKey.toLowerCase()] ||
                prevScaleKey.toLowerCase();

              payload[normalizedKey] = {
                ...(formData[normalizedKey] || {}),
                ...scaleData,
              };
            }
          }

          if (payload.nihss) {
            payload.nihss.score = Object.entries(payload.nihss)
              .filter(
                ([key]) =>
                  !["score", "interpretation", "comments"].includes(key)
              )
              .reduce((sum, [, value]) => sum + Number(value || 0), 0);
          }

          await submitWithPayload({
            ...payload,
            admission_id: admissionId || formData?.admission_id,
          });

          setActiveTab(nextKey);
          setActiveScaleKey(nextScaleKey);
          setIsDirty(false);
        },
        onCancel: () => {
          // Discard changes and proceed to the clicked tab
          setFormData(lastSavedRef.current || initialData);
          setActiveTab(nextKey);
          setActiveScaleKey(nextScaleKey);
          setIsDirty(false);
        },
      });
    } else {
      setActiveTab(nextKey);
      setActiveScaleKey(nextScaleKey);
    }
  };

  // Intercept close
  const handleCloseWithCheck = () => {
    const actuallyDirty =
      isDirty ||
      JSON.stringify(formData || {}) !==
        JSON.stringify(lastSavedRef.current || initialData || {});

    if (actuallyDirty) {
      opConfirmModal({
        title: "Unsaved changes",
        content:
          "You have unsaved changes. Do you want to save these changes before closing?",
        okText: "Save & Close",
        cancelText: "Close without saving",
        okButtonProps: {
          style: {
            backgroundColor: token.colorPrimary,
            borderColor: token.colorPrimary,
            color: "#fff",
          },
        },
        onOk: async () => {
          const actualAdmissionId = admissionId || formData?.admission_id;
          if (!actualAdmissionId) {
            message.error("Admission ID is required.");
            return;
          }

          let payload = { ...formData, admission_id: actualAdmissionId };

          if (activeScaleKey && scaleSaveRef.current) {
            const scaleData = await scaleSaveRef.current.saveScale();
            if (scaleData) {
              const normalizedKey =
                SCALE_KEY_NORMALIZER[activeScaleKey.toLowerCase()] ||
                activeScaleKey.toLowerCase();
              payload[normalizedKey] = {
                ...(payload[normalizedKey] || {}),
                ...scaleData,
              };
            }
          }

          await submitWithPayload(payload);
          setIsDirty(false);
          handleClose();
        },
        onCancel: () => {
          // Discard changes and close
          setFormData(lastSavedRef.current || initialData);
          setIsDirty(false);
          handleClose();
        },
      });
    } else {
      handleClose();
    }
  };

  const handleSubmit = async () => {
    try {
      const actualAdmissionId = admissionId || formData?.admission_id;

      if (!actualAdmissionId) {
        message.error("Admission ID is required.");
        return;
      }

      const payload = { ...formData, admission_id: actualAdmissionId };
      delete payload.TrunkControlTest;
      delete payload.barthel_index;
      const method = payload?.id ? "PATCH" : "POST";

      const response = await opServiceAPI("neuro", payload, method);
      const returned = response?.data?.data;

      message.success(
        method === "POST" ? "Neuro EMR saved." : "Neuro EMR updated."
      );

      setFormData((prev) => ({
        ...prev,
        ...payload,
        ...returned,
        id: returned?.id ?? prev?.id,
      }));
      lastSavedRef.current = {
        ...(lastSavedRef.current || {}),
        ...payload,
        ...returned,
        id: returned?.id ?? formData?.id,
      };

      if (onSave && activeTab === "22") {
        onSave(payload);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to save Neuro EMR.");
    }
  };

  const submitWithPayload = async (payload) => {
    try {
      if (payload && typeof payload === "object") {
        delete payload.TrunkControlTest;
        delete payload.barthel_index;
      }
      const method = payload?.id ? "PATCH" : "POST";
      const response = await opServiceAPI("neuro", payload, method);

      const returned = response?.data?.data;

      setFormData((prev) => ({
        ...prev,
        ...payload,
        ...returned,
      }));
      lastSavedRef.current = {
        ...(lastSavedRef.current || {}),
        ...payload,
        ...returned,
      };

      const tabLabel = getTabLabel(activeTab);
      message.success(`${tabLabel} saved successfully`);

      setIsDirty(false);
    } catch (error) {
      message.error("Failed to save form.");
    }
  };
  const handleButton = async () => {
    const actualAdmissionId = admissionId || formData?.admission_id;

    // ✅ FINAL SAVE (Summary tab)
    if (activeTab === "22") {
      await handleSubmit();
      handleClose();
      return;
    }
    let scaleData = null;

    if (activeScaleKey && scaleSaveRef.current) {
      scaleData = await scaleSaveRef.current.saveScale();
    }

    const payload = {
      ...formData,
      admission_id: actualAdmissionId,
    };

    if (activeScaleKey && scaleData) {
      const normalizedKey =
        SCALE_KEY_NORMALIZER[activeScaleKey.toLowerCase()] ||
        activeScaleKey.toLowerCase();

      payload[normalizedKey] = {
        ...(formData[normalizedKey] || {}),
        ...scaleData,
      };
    }

    if (payload.nihss) {
      payload.nihss.score = Object.entries(payload.nihss)
        .filter(([key]) => !["score", "interpretation"].includes(key))
        .reduce((sum, [, value]) => sum + Number(value || 0), 0);
    }
    await submitWithPayload(payload);

    const nextTab = (parseInt(activeTab) + 1).toString();
    setActiveTab(nextTab);

    if (SCALE_TAB_MAP[nextTab]) {
      setActiveScaleKey(SCALE_TAB_MAP[nextTab]);
    } else {
      setActiveScaleKey(null);
    }
  };

  const renderTab = () => {
    if (activeScaleKey) {
      return (
        <PhysioScalesOp
          key={activeScaleKey}
          ref={scaleSaveRef}
          admission={admissionId}
          activeScaleKey={activeScaleKey}
          scalesId={scalesId}
          setScalesId={setScalesId}
          scaleTab={scaleTab}
          setScaleTab={setScaleTab}
          notifyOnSave={true}
          onScaleDirty={() => setIsDirty(true)}
          onScalesSubmit={(key, data) => {
            if (typeof data !== "object") return;
            const { __closeAfterSave, ...cleanData } = data;
            const normalizedKey =
              SCALE_KEY_NORMALIZER[key.toLowerCase()] || key.toLowerCase();

            setFormData((prev) => ({
              ...prev,
              [normalizedKey]: cleanData,
            }));

            setIsDirty(true);
            if (__closeAfterSave) {
              setActiveScaleKey(null);
            }
          }}
          editData={formData}
          op={true}
          isNeuro={true}
          onClose={handleCloseWithCheck}
          patientData={patientData}
        />
      );
    }

    switch (activeTab) {
      case "1":
        return (
          <ChiefComplaintsForm
            onValuesChange={onValuesChange}
            formData={formData}
            formType="neuro"
          />
        );

      case "2":
        return (
          <OpHistoryForm onValuesChange={onValuesChange} formData={formData}>
            <NeuroHistory />
          </OpHistoryForm>
        );

      case "3":
        return (
          <OPExaminationForm
            onValuesChange={onValuesChange}
            formData={formData}
          />
        );
      case "12":
        return (
          <OpReflexesForm onValuesChange={onValuesChange} formData={formData} />
        );

      case "18":
        return (
          <OpFunctionalDiagnosis
            onValuesChange={onValuesChange}
            formData={formData}
            formType="neuro"
          />
        );

      case "19":
        return (
          <NeuroTherapy onValuesChange={onValuesChange} formData={formData} />
        );

      case "20":
        return (
          <OpExerciseForm onValuesChange={onValuesChange} formData={formData} />
        );

      case "21":
        return (
          <ReviewNotes onValuesChange={onValuesChange} formData={formData} />
        );

      case "22":
        return <NeuroSummary formData={formData} />;

      default:
        return null;
    }
  };

  return (
    <>
      <div className="op-assessment-tabs" style={{ background: "white" }}>
        {/* <h2 style={{ margin: 0, fontWeight: 600, textAlign: "center" }}>
          Neuro EMR
        </h2> */}
        <Tabs
          items={tabItems}
          activeKey={activeTab}
          onChange={handleTabChange}
          moreIcon={null}
        />
      </div>

      <div
        className="op-assessment-panel"
        style={{
          height: "calc(100vh - 110px)", // subtract header + bottom fixed footer
          overflowY: "auto",
          backgroundColor: "#f6f5ff",
          borderRadius: "5px",
          border: "1px solid #e6e6e6",
          paddingBottom: "120px", // space for fixed footer
        }}
      >
        <div className="ia op-assessment-form-body">{renderTab()}</div>
      </div>
      <div
        style={{
          position: "fixed",
          bottom: 0,
          right: 0,
          width: "100%",
          background: "#fff",
          padding: "10px 20px",
          borderTop: "1px solid #e6e6e6",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          zIndex: 1000,
        }}
      >
        <Button onClick={handleCloseWithCheck}>Cancel</Button>
        <Button type="primary" onClick={handleButton}>
          {activeTab === "22" ? "Save" : "Next"}
        </Button>
      </div>
    </>
  );
};

export default NeuroForm;
